import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Grid,
  Tab,
  Typography,
  Breadcrumbs,
  Link
} from '@mui/material';
import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import RelaunchStats from '../../components/dashboard/relaunch/RelaunchStats';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import ChevronRightIcon from '../../icons/ChevronRight';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import { alpha } from '@mui/material/styles';
import { lightVeil, darkVeil, greyAlpha, grey } from '../../theme/tones';

const Reminders = () => {
  const theme = useTheme();
  const [tabs, setTabs] = React.useState('all');

  const handleChange = (event, tab) => {
    setTabs(tab);
  };

  return (
    <>
      <Helmet>
        <title>Relances | Syndays</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'light' ? grey(50) : grey(950),
          minHeight: '100%',
          py: 4,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Soft glowing background blur spots for premium design */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            left: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0)} 70%)`,
            pointerEvents: 'none',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 200,
            right: -100,
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0)} 70%)`,
            pointerEvents: 'none',
            zIndex: 0
          }}
        />
        <Container maxWidth={false} disableGutters sx={{ px: 2, position: 'relative', zIndex: 1 }}>
          {/* ── Hero Header ── */}
          <Box
            sx={{
              mb: 4,
              p: 3.5,
              borderRadius: '24px',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              backdropFilter: 'blur(20px)',
              boxShadow: theme.palette.mode === 'light'
                ? `0 4px 24px ${darkVeil(0.04)}`
                : `0 4px 24px ${darkVeil(0.2)}`,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
                  flexShrink: 0,
                }}
              >
                <ArrowCircleUpIcon sx={{ color: grey(0), fontSize: '1.7rem' }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    color: 'text.primary',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  Relances clients
                </Typography>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                  sx={{ mt: 1 }}
                >
                  <Link
                    color="textSecondary"
                    component={RouterLink}
                    to="/dashboard"
                    variant="subtitle2"
                    sx={{ textDecoration: 'none', '&:hover': { color: 'secondary.main' } }}
                  >
                    Tableau de bord
                  </Link>
                  <Typography
                    color="textSecondary"
                    variant="subtitle2"
                  >
                    Relances
                  </Typography>
                </Breadcrumbs>
              </Box>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TabContext value={tabs}>
                <Box
                  sx={{
                    mb: 4
                  }}
                >
                  <TabList
                    onChange={handleChange}
                    aria-label="Relaunch Tabs"
                    sx={{
                      backgroundColor: theme.palette.mode === 'light' ? greyAlpha(100, 0.8) : greyAlpha(800, 0.5),
                      backdropFilter: 'blur(8px)',
                      borderRadius: '16px',
                      p: 0.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      minHeight: 'auto',
                      display: 'inline-flex',
                      '& .MuiTabs-flexContainer': {
                        gap: 0.5
                      },
                      '& .MuiTabs-indicator': {
                        height: '100%',
                        borderRadius: '12px',
                        backgroundColor: theme.palette.mode === 'light' ? grey(0) : lightVeil(0.08),
                        boxShadow: theme.palette.mode === 'light' ? `0 4px 12px ${darkVeil(0.05)}` : 'none',
                        border: theme.palette.mode === 'light' ? 'none' : `1px solid ${lightVeil(0.1)}`
                      },
                      '& .MuiTab-root': {
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        textTransform: 'none',
                        minWidth: 80,
                        minHeight: 40,
                        py: 1,
                        px: 2.5,
                        borderRadius: '12px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        zIndex: 1,
                        color: 'text.secondary',
                        '&.Mui-selected': {
                          color: 'primary.main',
                        },
                        '&:hover': {
                          color: 'primary.main',
                          opacity: 0.9,
                          transform: 'translateY(-1px)'
                        }
                      }
                    }}
                  >
                    <Tab label="Tous" value="all" />
                    <Tab label="Formation continue" value="fc" />
                    <Tab label="Tour 1" value="turn1" />
                    <Tab label="Tour 2" value="turn2" />
                    <Tab label="Décalage" value="shift" />
                  </TabList>
                </Box>

                <TabPanel value="all" sx={{ p: 0 }}>
                  <RelaunchStats state="all" />
                </TabPanel>
                <TabPanel value="fc" sx={{ p: 0 }}>
                  <RelaunchStats state="fc" />
                </TabPanel>
                <TabPanel value="turn1" sx={{ p: 0 }}>
                  <RelaunchStats state="turn1" />
                </TabPanel>
                <TabPanel value="turn2" sx={{ p: 0 }}>
                  <RelaunchStats state="turn2" />
                </TabPanel>
                <TabPanel value="shift" sx={{ p: 0 }}>
                  <RelaunchStats state="shift" />
                </TabPanel>
              </TabContext>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Reminders;
